// Shared core of the endpoint-inventory table — column schema, row
// flattening, cell rendering, and the filter/sort helpers — extracted
// from EndpointInventoryPage so the Explore "Inventory" lens renders
// the exact same table scoped to the current scope's endpoints without
// the two surfaces drifting.
//
// Backed by the legacy REST endpoint `GET /v1/endpoints/metadata`
// (fetchInventoryPage below). Rows flatten the nested
// EndpointMetadataInfo snapshot into a flat per-endpoint record.

import { restGet } from "../../lib/restClient.js";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { AgentIcon } from "../Dimension.jsx";
import { agentDisplayLabel } from "../../lib/agentIdentity.js";
import { timeLabelFormatter, dayLabelFormatter } from "../../lib/traceUtils.js";
import { endpointIdentityBadge } from "../../lib/endpointDedupe.js";
import {
    INVENTORY_SEARCHABLE_FIELDS,
    endpointIdToUuid,
    flattenInventoryRow,
    filterInventoryRows,
    sortInventoryRows,
} from "../../lib/inventoryRows.js";

// Pure row logic (id normalization, flattening, filter/sort) lives in
// lib/inventoryRows.js so it's node-testable; re-exported here so both
// consumers keep one import site.
export {
    INVENTORY_SEARCHABLE_FIELDS,
    endpointIdToUuid,
    flattenInventoryRow,
    filterInventoryRows,
    sortInventoryRows,
};

// ── Column schema (mirrors origin/dashboard) ─────────────────────
//
// Ordering rationale:
//   1. The at-a-glance columns sit contiguously at the start in a natural
//      "what is this / when did we hear from it / what's running" reading
//      order (hostname → last seen → operating system → registered user
//      → agent version).
//   2. The remaining columns continue the same theme: more software/agent
//      details, then identity (serial), then hardware specs (manufacturer
//      → drive free), then deeper provenance (installed agents + SHAs).
//      "Installed AI agents" is also default-visible (see
//      INVENTORY_DEFAULT_VISIBLE) even though it lives in the provenance
//      group — it answers the "what's running" question the inventory
//      exists to surface.
export const INVENTORY_COLUMNS = [
    // ── Default-visible (the at-a-glance columns) ──
    { id: "hostname",                  label: "Hostname",              width: 180 },
    { id: "snapshotTime",              label: "Last seen",             width: 130 },
    { id: "platform",                  label: "Operating system",      width: 120 },
    { id: "registeredUser",            label: "Registered user",       width: 150 },
    { id: "agentVersion",              label: "Agent version",         width: 110 },
    // ── Software ──
    { id: "watchdogVersion",           label: "Watchdog version",      width: 120 },
    // osDisplayVersion / osEdition are DERIVED client-side from operating_system +
    // the os_version map (see deriveOsVersionCells), so there's no single server
    // column to order the whole fleet by — not server-sortable.
    { id: "osDisplayVersion",          label: "OS version",            width: 110, sortable: false },
    { id: "osEdition",                 label: "OS edition",            width: 100, sortable: false },
    // ── Identity ──
    { id: "serial",                    label: "Serial",                width: 150 },
    // ── Hardware ──
    { id: "manufacturer",              label: "Manufacturer",          width: 130 },
    { id: "model",                     label: "Model",                 width: 160 },
    { id: "processorModel",            label: "Processor model",       width: 200 },
    { id: "coreCount",                 label: "Cores",                 width: 80 },
    { id: "systemMemoryMb",            label: "Memory",                width: 100 },
    { id: "primaryDriveDiskSizeGb",    label: "Drive size",            width: 100 },
    { id: "primaryDriveFreeSpaceGb",   label: "Drive free",            width: 100 },
    // ── Deeper provenance ──
    // installedAiAgents is a deduped, joined string built client-side from the
    // installed_ai_agents JSON — not a single orderable server column.
    { id: "installedAiAgents",         label: "Installed AI agents",   width: 220, sortable: false },
    { id: "agentExecutableSha256",     label: "Agent SHA256",          width: 140 },
    { id: "watchdogExecutableSha256",  label: "Watchdog SHA256",       width: 140 },
];

export const INVENTORY_DEFAULT_VISIBLE = ["hostname", "snapshotTime", "platform", "registeredUser", "agentVersion", "installedAiAgents"];

// ── Cell content ─────────────────────────────────────────────────

export function renderInventoryCell(row, col) {
    const v = row[col.id];
    if (col.id === "hostname") {
        const badge = endpointIdentityBadge(row);
        return (
            <div className="flex min-w-0 items-center gap-[6px]">
                {v == null || v === "" ? (
                    <span className="text-[color:var(--muted-soft)]">—</span>
                ) : (
                    <OverflowTooltip
                        as="strong"
                        text={String(v)}
                        className="min-w-0 text-[11px] leading-[1.08] font-medium"
                    />
                )}
                {badge ? (
                    <span
                        title={badge.title}
                        className="shrink-0 rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--control-bg)] px-[4px] py-[1px] text-[8px] uppercase tracking-[0.06em] text-[color:var(--muted)]"
                    >
                        {badge.label}
                    </span>
                ) : null}
            </div>
        );
    }

    if (v == null || v === "") return <span className="text-[color:var(--muted-soft)]">—</span>;

    if (col.id === "snapshotTime" && v instanceof Date) {
        return (
            <div className="grid gap-0">
                <strong className="text-[10px] leading-[1.12] font-medium">
                    {timeLabelFormatter.format(v)}
                </strong>
                <span className="text-[10px] leading-[1.12] text-[color:var(--muted-soft)]">
                    {dayLabelFormatter.format(v)}
                </span>
            </div>
        );
    }

    // Installed AI agents — render names in ink, versions muted, so
    // the eye lands on which tools are present and the version reads
    // as a secondary tag. Same idea as snapshotTime's time/day stack.
    // The tooltip carries the same color treatment but stacks each
    // agent on its own line for readability.
    if (col.id === "installedAiAgents") {
        const list = row.installedAiAgentsList;
        if (!Array.isArray(list) || list.length === 0) {
            return <span className="text-[color:var(--muted-soft)]">—</span>;
        }
        // Product names ("Claude Code", "Ollama") render verbatim; only the
        // known legacy enum strings ("ChatgptDesktop", "Openrouter") get
        // humanized. Folding through deriveAgentIdentity here erased the
        // Code-vs-Desktop distinction the agent now reports.
        const display = (raw) => agentDisplayLabel(raw);
        // A tool with >1 installed version shows only its latest inline, marked
        // with `*`; the hover popup lists every version, one per line.
        const hasMultiVersion = list.some((a) => (a.versions?.length ?? 0) > 1);
        const visible = (
            <>
                {list.map((a, i) => (
                    <span key={i}>
                        {i > 0 ? (
                            <span className="text-[color:var(--muted-soft)]">, </span>
                        ) : null}
                        <AgentIcon
                            name={a.name}
                            className="inline-block h-2.5 w-2.5 mr-1 align-text-bottom"
                        />
                        <span className="text-[color:var(--ink)]">{display(a.name)}</span>
                        {a.version ? (
                            <span className="text-[color:var(--muted-soft)]"> {a.version}</span>
                        ) : null}
                        {(a.versions?.length ?? 0) > 1 ? (
                            <span className="text-[color:var(--ink)]">*</span>
                        ) : null}
                    </span>
                ))}
            </>
        );
        const tooltipBlock = (
            <div className="grid gap-[3px]">
                {list.map((a, i) => {
                    const multi = (a.versions?.length ?? 0) > 1;
                    return (
                        <div key={i} className="leading-[1.2]">
                            <AgentIcon
                                name={a.name}
                                className="inline-block h-2.5 w-2.5 mr-1 align-text-bottom"
                            />
                            <span className="text-[color:var(--ink)] font-medium">{display(a.name)}</span>
                            {!multi && a.version ? (
                                <span className="text-[color:var(--muted-soft)]"> {a.version}</span>
                            ) : null}
                            {multi ? (
                                <div className="pl-[18px]">
                                    {a.versions.map((v) => (
                                        <div
                                            key={v}
                                            className="text-[color:var(--muted-soft)] leading-[1.25]"
                                        >
                                            {v}
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                            {/* Set only when another entry in this row renders the same
                                label: two installs an older agent could only describe by
                                a coarse provider tag are otherwise identical lines. */}
                            {a.distinguisher ? (
                                <div className="pl-[18px] text-[color:var(--muted-soft)] leading-[1.25] break-all">
                                    {a.distinguisher}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        );
        return (
            <OverflowTooltip
                as="strong"
                text={row.installedAiAgents}
                tooltipContent={tooltipBlock}
                always={hasMultiVersion}
                className="text-[11px] leading-[1.08] font-medium"
            >
                {visible}
            </OverflowTooltip>
        );
    }

    let text;
    if (col.id === "systemMemoryMb") {
        const gb = Number(v) / 1024;
        text = gb >= 1 ? `${gb.toFixed(1)} GB` : `${v} MB`;
    } else if (col.id === "primaryDriveDiskSizeGb" || col.id === "primaryDriveFreeSpaceGb") {
        text = `${v} GB`;
    } else if (col.id === "agentExecutableSha256" || col.id === "watchdogExecutableSha256") {
        text = typeof v === "string" && v.length > 12 ? `${v.slice(0, 12)}…` : v;
    } else {
        text = String(v);
    }

    return (
        <OverflowTooltip
            as="strong"
            text={text}
            className="text-[11px] leading-[1.08] font-medium"
        />
    );
}

// ── Fetch ────────────────────────────────────────────────────────

// Mirrors `TraceLogPanel`'s PAGE constant. The metadata endpoint
// caps page_size at 1000; for typical orgs (a few hundred endpoints)
// this means the first fetch is the whole inventory.
export const INVENTORY_PAGE_SIZE = 100;

// Maps a table column id to its server sort field — the `EndpointMetadataSortField`
// proto enum value name (grpc-gateway also accepts the integer). Columns absent
// here are display-derived client-side (osDisplayVersion, osEdition,
// installedAiAgents) and carry `sortable: false` above, so the header offers no
// sort control and this map never needs an entry for them.
export const COLUMN_SORT_FIELD = {
    snapshotTime:             "ENDPOINT_METADATA_SORT_FIELD_LAST_SEEN",
    hostname:                 "ENDPOINT_METADATA_SORT_FIELD_HOSTNAME",
    registeredUser:           "ENDPOINT_METADATA_SORT_FIELD_REGISTERED_USER",
    platform:                 "ENDPOINT_METADATA_SORT_FIELD_OPERATING_SYSTEM",
    agentVersion:             "ENDPOINT_METADATA_SORT_FIELD_AGENT_VERSION",
    watchdogVersion:          "ENDPOINT_METADATA_SORT_FIELD_WATCHDOG_VERSION",
    serial:                   "ENDPOINT_METADATA_SORT_FIELD_SERIAL",
    manufacturer:             "ENDPOINT_METADATA_SORT_FIELD_MANUFACTURER",
    model:                    "ENDPOINT_METADATA_SORT_FIELD_MODEL",
    processorModel:           "ENDPOINT_METADATA_SORT_FIELD_PROCESSOR_MODEL",
    coreCount:                "ENDPOINT_METADATA_SORT_FIELD_CORE_COUNT",
    systemMemoryMb:           "ENDPOINT_METADATA_SORT_FIELD_SYSTEM_MEMORY",
    primaryDriveDiskSizeGb:   "ENDPOINT_METADATA_SORT_FIELD_DRIVE_SIZE",
    primaryDriveFreeSpaceGb:  "ENDPOINT_METADATA_SORT_FIELD_DRIVE_FREE",
    agentExecutableSha256:    "ENDPOINT_METADATA_SORT_FIELD_AGENT_SHA256",
    watchdogExecutableSha256: "ENDPOINT_METADATA_SORT_FIELD_WATCHDOG_SHA256",
};

// `search` is an optional case-insensitive substring filter, and `sortBy`
// ({ id, dir }) is the column sort — BOTH applied server-side across the whole
// tenant (not just the loaded page), so the returned `total` reflects the
// matching count and the ordering is correct beyond page 0. `restGet` drops
// empty/null params, so: no search ⇒ lists everything; an unmapped/absent sort
// column ⇒ server default (last-seen, newest first); ascending ⇒ no sort_desc.
export async function fetchInventoryPage(page, search = "", sortBy = null) {
    const sortField = sortBy ? COLUMN_SORT_FIELD[sortBy.id] : null;
    const resp = await restGet("/v1/endpoints/metadata", {
        page,
        page_size: INVENTORY_PAGE_SIZE,
        search,
        sort_field: sortField || "",
        sort_desc: sortField && sortBy.dir === "desc" ? true : "",
    });
    return {
        rows: (resp?.metadata ?? []).map(flattenInventoryRow),
        total: resp?.total ?? null,
    };
}
